import { cx } from '@linaria/core';
import { memo, useId, useRef, useState, useCallback, useEffect, Suspense } from 'react';
import { pm2SitemapStore, findResourcePageTree, findCourseDescriptorFromParents } from '@pm2/sitemap-utils';
import { pm2InterfaceStore } from '@pm2/store';
import { useNavigationManager } from '@provider/navigation';
import { breakpoints } from '@ui/css';
import { Pill, GuideBubble } from '@ui/kit';
import { BulbIcon } from '@ui/kit/icons';
import { useMatchBreakpoint, useLoaderState, useForceHeaderExpanded, useScrollLocking, CSSRefTransition, IsomorphicPortal } from '@utils/react';
import { button } from './atom.favourite-button.style.js';
import { buttonWrapperCls, iconWrapper, lightbulb, throbberCls, throbberWrapperCls, guideBubbleCls } from './atom.resource-button.style.js';
import { ResourceDropdown } from './molecule.resource-dropdown.js';
import { jsxs, Fragment, jsx } from 'react/jsx-runtime';

function ResourceButtonNonMemoized({
  activePage
}) {
  const dropdownId = useId();
  const isMobile = useMatchBreakpoint(breakpoints.mobile);
  const buttonRef = useRef(null);
  const navigation = useNavigationManager();
  const [isReady, setReady] = useState(false);
  const sitemap = pm2SitemapStore.useSelector(state => state);
  const showResources = pm2InterfaceStore.useSelector(state => state.showResources);
  const showLoader = useLoaderState(!!showResources && !isReady);
  const resourcePageTree = activePage ? findResourcePageTree(sitemap, activePage) : [];
  useForceHeaderExpanded(!isMobile && !!showResources);
  useScrollLocking(!!showResources);
  const handleResourceDropdownOpen = useCallback(() => {
    // consider the resources ready once the dropdown has rendered (that means
    // that the resources have been loaded and suspense no longer throws)
    setReady(true);
    focusResourceContent();
  }, []);
  const handleResourceDropdownClose = useCallback(() => {
    pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.toggleResources(false));
    restoreFocusToButton(buttonRef);
  }, []);
  const handleButtonClick = () => {
    pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.toggleResources());
    if (!showResources) {
      focusResourceContent();
    } else {
      restoreFocusToButton(buttonRef);
    }
  };

  // Allow Breadcrumb to adjust its opacity when resources gets opened to be
  // fully opaque
  useEffect(() => {
    document.body.classList.toggle("openResources", showResources && isReady);
  }, [showResources, isReady]);

  // auto-collapse whenever user navigates
  useEffect(() => {
    return navigation.subscribe(() => {
      pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.toggleResources(false));
    });
  }, [navigation]);

  // auto-collapse and assume that resources haven't been fetched whenever the
  // list of resources are changed
  useEffect(() => {
    pm2InterfaceStore.dispatch(pm2InterfaceStore.actions.toggleResources(false));
    setReady(false);
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [JSON.stringify(resourcePageTree.map(it => it.id))]);
  if (!activePage) {
    return null;
  }
  const activePageOrCourse = findCourseDescriptorFromParents(sitemap, activePage) ?? activePage;
  return /*#__PURE__*/jsxs(Fragment, {
    children: [/*#__PURE__*/jsx(CSSRefTransition, {
      in: resourcePageTree.length > 0,
      timeout: 0,
      unmountOnExit: true,
      mountOnEnter: true,
      children: ref => /*#__PURE__*/jsxs("div", {
        ref: ref,
        className: buttonWrapperCls,
        children: [/*#__PURE__*/jsx(Pill, {
          className: button,
          onClick: handleButtonClick,
          ref: buttonRef,
          "aria-label": showResources ? "Luk ressourcepanel" : "Åbn ressourcepanel",
          active: showResources,
          activeTheme: "accent",
          size: "compact",
          icon: /*#__PURE__*/jsxs("span", {
            className: iconWrapper,
            children: [/*#__PURE__*/jsx(BulbIcon, {
              className: cx(lightbulb, showLoader.current && "hidden")
            }), /*#__PURE__*/jsx("span", {
              className: cx(throbberWrapperCls, !showLoader.current && "hidden"),
              children: /*#__PURE__*/jsx("span", {
                className: throbberCls
              })
            })]
          }),
          "aria-expanded": showResources,
          "aria-controls": dropdownId,
          children: !isMobile && /*#__PURE__*/jsx("span", {
            children: "Ressourcer"
          })
        }), /*#__PURE__*/jsx(GuideBubble, {
          id: "resources",
          className: guideBubbleCls,
          popperPlacement: "bottom",
          popperOffsetPx: 8,
          label: "Læs mere om brugen af resourcer her",
          title: "Ressourcer",
          hasUserInteracted: showResources !== undefined,
          children: "Klik p\xE5 ressourcer og f\xE5 et overblik over de ressourcer, der er knyttet til forl\xF8bet eller siden. Du kan altid finde ressource-knappen og de tilg\xE6ngelige ressourcer her."
        })]
      })
    }), /*#__PURE__*/jsx(IsomorphicPortal, {
      children: /*#__PURE__*/jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/jsx(ResourceDropdown, {
          id: dropdownId,
          expanded: !!showResources,
          ready: isReady,
          activePage: activePage,
          activePageTitle: activePageOrCourse.title,
          resourceButtonRef: buttonRef,
          onOpen: handleResourceDropdownOpen,
          onClose: handleResourceDropdownClose
        })
      })
    })]
  });
}
const ResourceButton = /*#__PURE__*/memo(ResourceButtonNonMemoized);

/**
 * Brings focus to the resource dropdown (it'll be opened in a portal, so
 * we need this in order to assist tab-index, screenreaders etc.)
 */
function focusResourceContent() {
  const resourceDropdownElement = document.querySelector("[data-is-resource-content=true]");
  resourceDropdownElement?.focus();
}
function restoreFocusToButton(buttonRef) {
  const button = buttonRef.current;
  button?.focus();
}

export { ResourceButton };
//# sourceMappingURL=atom.resource-button.js.map
